import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSeoMeta, useSocialMeta } from '@/hooks/usePageTitle';
import { useLocale } from '@/hooks/useLocale';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, ChevronRight, Sparkles } from 'lucide-react';
import { ISO_2022_GUIDANCE } from '@/lib/isoData';

const MATURITY_BADGE_CLASS = {
  low: 'bg-red-50 text-red-600 border-red-200',
  medium: 'bg-[#B7791F]/[0.06] text-[#B7791F] border-[#B7791F]/25',
  high: 'bg-[#F1F8E8] text-[#527F19] border-[#527F19]/25',
};
const MATURITY_LABEL_KEY = {
  low: 'guidance.lowMaturity',
  medium: 'guidance.mediumMaturity',
  high: 'guidance.highMaturity',
};
const MATURITY_LEVELS = ['low', 'medium', 'high'];

export default function GuidancePage() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  useSeoMeta(t('seo.pages.guidanceTitle'), t('seo.pages.guidanceDescription'));
  useSocialMeta({
    title: t('seo.pages.guidanceTitle'),
    description: t('seo.pages.guidanceDescription'),
    url: `https://ismscalculator.com/${lang}/guidance`,
  });
  const [selectedTheme, setSelectedTheme] = useState(ISO_2022_GUIDANCE[0]?.id || 'organizational');
  const theme = ISO_2022_GUIDANCE.find(d => d.id === selectedTheme) || ISO_2022_GUIDANCE[0];
  const newGroups = theme.groups.filter(g => g.isNew);

  return (
    <AppShell>
      <div className="workspace-main-width guidance-workspace">
        <div className="mb-8">
          <h1
            className="text-2xl sm:text-3xl font-semibold text-[#171A1D]"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            data-testid="guidance-heading"
          >
            {t('guidance.title')}
          </h1>
          <p className="mt-2 text-sm text-[#66707A]">
            {t('guidance.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar — the 4 ISO 27001:2022 themes */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="space-y-1 pr-1">
              {ISO_2022_GUIDANCE.map(d => (
                <button
                  key={d.id}
                  onClick={() => setSelectedTheme(d.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    selectedTheme === d.id
                      ? 'bg-[#527F19]/10 text-[#527F19] font-medium'
                      : 'text-[#66707A] hover:text-[#171A1D] hover:bg-white'
                  }`}
                  data-testid={`guide-domain-${d.id}`}
                >
                  <span className="flex items-center gap-2">
                    <ChevronRight className={`w-3 h-3 shrink-0 transition-transform ${selectedTheme === d.id ? 'rotate-90' : ''}`} />
                    <span className="truncate">{d.code} {t('iso2022.themes.' + d.id)}</span>
                    <span className="ml-auto text-[10px] font-mono text-[#8A929B]">{d.controlCount}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <Card className="bg-white border-[#DDE1E5]" data-testid="guidance-content">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-[#527F19] text-[#527F19]">
                  {theme.code}
                </Badge>
                <Badge variant="outline" className="border-[#DDE1E5] text-[#66707A] font-mono text-[10px]">
                  {theme.controlCount} {t('iso2022.controls')}
                </Badge>
              </div>
              <CardTitle className="text-xl mt-2 text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('iso2022.themes.' + theme.id)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-[#66707A] uppercase tracking-wide mb-2">
                  {t('guidance.overview')}
                </h3>
                <p className="text-sm text-[#3A4249] leading-relaxed">
                  {t('guidance.themes.' + theme.id + '.description')}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#66707A] uppercase tracking-wide mb-2">
                  {t('guidance.keyControls')}
                </h3>
                <ul x-excluded="true" className="space-y-1.5">
                  {theme.groups.map((group) => (
                    <li key={group.id} className="text-sm text-[#3A4249] flex items-start gap-2" data-testid={`guide-control-${group.id}`}>
                      <span className="text-[#527F19] mt-0.5 shrink-0">&#8226;</span>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>{t('iso2022.groups.' + group.id)}</span>
                        <span className="text-[10px] font-mono text-[#8A929B]">{group.code}</span>
                        {group.isNew && (
                          <Badge
                            variant="outline"
                            className="h-4 px-1.5 text-[9px] font-semibold uppercase tracking-wide border-[#2867B2]/35 text-[#2867B2] bg-[#2867B2]/[0.06]"
                          >
                            <Sparkles className="w-2.5 h-2.5 mr-0.5" /> {t('iso2022.new')}
                          </Badge>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {newGroups.length > 0 && (
                <div className="p-4 rounded-lg bg-[#2867B2]/[0.06] border border-[#2867B2]/25" data-testid="new-2022-callout">
                  <h3 className="text-sm font-semibold text-[#2867B2] flex items-center gap-1.5 mb-2">
                    <Sparkles className="w-4 h-4" /> {t('guidance.newControls')}
                  </h3>
                  <ul x-excluded="true" className="space-y-1">
                    {newGroups.map((group) => (
                      <li key={group.id} className="text-xs text-[#1F5391] leading-relaxed">
                        {t('iso2022.newControls.' + group.id)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-[#66707A] uppercase tracking-wide mb-3">
                  {t('guidance.maturityTips')}
                </h3>
                <div className="space-y-3">
                  {MATURITY_LEVELS.map((level) => (
                    <div key={level} className="p-4 rounded-lg bg-[#F5F6F7] border border-[#DDE1E5]">
                      <Badge
                        className={`mb-2 ${MATURITY_BADGE_CLASS[level]}`}
                        variant="outline"
                      >
                        {t(MATURITY_LABEL_KEY[level])}
                      </Badge>
                      <p className="text-sm text-[#3A4249] leading-relaxed">
                        {t('guidance.themes.' + theme.id + '.tips.' + level)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {theme.resources?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#66707A] uppercase tracking-wide mb-2">
                    {t('guidance.resources')}
                  </h3>
                  <div className="space-y-2">
                    {theme.resources.map((res, i) => (
                      <a
                        key={res.url}
                        href={res.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#527F19] hover:underline"
                        data-testid={`resource-link-${i}`}
                      >
                        <ExternalLink className="w-3 h-3 shrink-0" />
                        {res.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
